import BudgetModes from '@/common/enums/budget-modes.enum';
import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
import { LineItem } from '@/common/models';
import { getLineItemCadenceOptimizedImpressions } from '@/org-app/components/lineItems/CadenceOptimizedImpressions';
import NumberHelper from '@/utils/NumberHelper';
import { mapOrgAppActions } from '@/utils/StateMapping';

import { EntityAvailsDataHelper } from './EntityAvailsDataHelper';

export class LineItemAvailsDataHelper extends EntityAvailsDataHelper {
  private getLineItemSummaryPanel: (data: {
    id: number;
    start: string;
    end: string;
  }) => Promise<{ impressions: number }>;

  constructor(protected lineItem: LineItem) {
    super(lineItem);
    const { getLineItemSummaryPanel } = mapOrgAppActions();
    this.getLineItemSummaryPanel = getLineItemSummaryPanel;
  }

  protected async fetchDeliveredImpressions(): Promise<number> {
    // If line item is not saved yet or line item is not running, return 0
    if (this.lineItem.isNew || !this.lineItem.isRunning) {
      return 0;
    }
    const data = {
      id: this.lineItem.id,
      start: '',
      end: '',
    };
    try {
      const response = await this.getLineItemSummaryPanel(data);
      const { impressions } = response;
      return impressions;
    } catch (e) {
      console.error(e);
      return 0;
    }
  }

  protected async getCPM(): Promise<number> {
    if (!this.lineItem.isCPMFromRateCard) {
      return this.lineItem.CPM;
    }
    const cpmInfo = await this.getCPMForTargets();
    return cpmInfo.average_cpm;
  }

  protected async fetchDeliveryGoal(): Promise<number> {
    try {
      // Cadence uses a different calculation for delivery goal
      if (this.lineItem.purchaseType === PurchaseTypesEnum.CADENCE) {
        // When we are in edit mode, targetImpressions reflect the value in the impression field
        if (this.lineItem.targetImpressions != null) {
          return this.lineItem.targetImpressions;
        }
        // When in detailed mode, we need to get the value from the cadence-optimized-impressions property (saved in Line Item properties)
        const cadenceOptimizedImpressions = await getLineItemCadenceOptimizedImpressions(this.lineItem);
        if (cadenceOptimizedImpressions != null) {
          return cadenceOptimizedImpressions;
        }
        // If cpp is null or zero, it means that the user has not entered a value in the "play per hour" field yet and we cannot calculate the delivery goal
        if (!NumberHelper.isSafeDivider(this.lineItem.cpp)) {
          return 0;
        } else {
          // In last resort, we calculate the delivery goal based on the budget and cpp
          return (this.lineItem.budget / this.lineItem.cpp) * 1000;
        }
      }

      if (this.lineItem.budgetMode === BudgetModes.IMPRESSIONS) {
        return this.lineItem.targetImpressions;
      }

      return await this.computeDeliveryGoalFromCpm(this.lineItem.budget);
    } catch (error) {
      console.error(error);
      return 0;
    }
  }
}
